import React, {useCallback, useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {SavReceptionItem, SavReserves} from '../../../../components/sav/Models'
import {
  envoyerSavMessageAutomatique,
  getSavRetoursAttendus,
  receptionnerSavRetour,
  rechercherSavReception,
  savErreur,
  uploadSavDocument,
} from '../../../../components/sav/requests'
import {formatSavDate, nomSavClient} from '../../../../components/sav/libelles'
import SavStatutBadge from '../components/SavStatutBadge'
import SavSourceIcone from '../components/SavSourceIcone'
import SavAc10 from '../components/SavAc10'
import {useSavReferentiels} from '../components/useSavReferentiels'

const RESERVES_VIDES: SavReserves = {
  reserveColisAbime: false,
  reservePieceManquante: false,
  reservePieceDifferente: false,
  reserveReception: '',
}
const CLE_SITE = 'sav.reception.site'

const lireSite = (): string => {
  try {
    return localStorage.getItem(CLE_SITE) ?? ''
  } catch {
    return ''
  }
}

// Écran 5 : réception quai, pensé pour une tablette 1024 × 768 et un usage au doigt
const SavReceptionPage = () => {
  const referentiels = useSavReferentiels()
  const [saisie, setSaisie] = useState('')
  const [resultats, setResultats] = useState<Array<SavReceptionItem> | null>(null)
  const [choisi, setChoisi] = useState<SavReceptionItem | null>(null)
  const [reserves, setReserves] = useState<SavReserves>(RESERVES_VIDES)
  const [photos, setPhotos] = useState<number>(0)
  const [attendus, setAttendus] = useState<Array<SavReceptionItem>>([])
  const [site, setSite] = useState<string>(lireSite())
  const [envoi, setEnvoi] = useState(false)
  const champ = useRef<HTMLInputElement>(null)
  const champPhoto = useRef<HTMLInputElement>(null)

  const chargerAttendus = useCallback(() => {
    getSavRetoursAttendus(site ? Number(site) : null)
      .then(setAttendus)
      .catch((e) => toast.error(savErreur(e)))
  }, [site])

  useEffect(() => {
    chargerAttendus()
    try {
      localStorage.setItem(CLE_SITE, site)
    } catch {
      // stockage indisponible : le site reste choisi pour la session
    }
  }, [chargerAttendus, site])

  useEffect(() => {
    champ.current?.focus()
  }, [])

  const choisir = (item: SavReceptionItem | null) => {
    setChoisi(item)
    setReserves(RESERVES_VIDES)
    setPhotos(0)
  }

  const chercher = (valeur = saisie) => {
    const q = valeur.trim()
    if (!q) return
    rechercherSavReception(q)
      .then((liste) => {
        setResultats(liste)
        choisir(liste.length === 1 ? liste[0] : null)
        if (!liste.length) toast.warning('Aucun retour attendu pour cette référence.')
      })
      .catch((e) => toast.error(savErreur(e)))
  }

  const ajouterPhoto = async (fichiers: FileList | null) => {
    if (!choisi || !fichiers?.length) return
    try {
      for (const fichier of Array.from(fichiers)) {
        await uploadSavDocument(choisi.dossier.id, fichier, 'PHOTO_COLIS')
      }
      setPhotos((n) => n + fichiers.length)
      toast.success('Photo enregistrée')
    } catch (e) {
      toast.error(savErreur(e))
    } finally {
      if (champPhoto.current) champPhoto.current.value = ''
    }
  }

  const confirmer = () => {
    if (!choisi) return
    setEnvoi(true)
    receptionnerSavRetour(choisi.retour.id, reserves)
      .then(async (d) => {
        toast.success(`${choisi.dossier.numero} reçu : pièces placées dans la file du labo`)
        // Spec 5.5 : le message « Colis reçu » part par mail sans relecture au quai
        if (d.messagePropose === 'RECEPTION') {
          const motif = await envoyerSavMessageAutomatique(choisi.dossier.id, 'RECEPTION').catch(
            (e) => savErreur(e)
          )
          if (motif) toast.warning(`Mail « Colis reçu » non envoyé : ${motif}.`)
        }
        choisir(null)
        setResultats(null)
        setSaisie('')
        chargerAttendus()
        champ.current?.focus()
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  const caseReserve = (cle: keyof Omit<SavReserves, 'reserveReception'>, libelle: string) => (
    <label
      className={`d-flex align-items-center gap-3 rounded p-4 flex-grow-1 cursor-pointer ${
        reserves[cle] ? 'bg-light-danger border border-danger' : 'bg-light'
      }`}
      style={{minHeight: 64}}
    >
      <input
        type='checkbox'
        className='form-check-input'
        style={{width: 28, height: 28}}
        checked={reserves[cle]}
        onChange={(e) => setReserves((r) => ({...r, [cle]: e.target.checked}))}
      />
      <span className='fs-5 fw-bold'>{libelle}</span>
    </label>
  )

  return (
    <div className='row g-5'>
      <div className='col-lg-8'>
        <div className='d-flex align-items-center gap-4 mb-4'>
          <SavAc10 etape='Retour SAV' acteur='Réception' delai='J' />
        </div>
        <div className='d-flex gap-3 mb-5'>
          <input
            ref={champ}
            className='form-control fs-3'
            style={{height: 60, border: '2px solid #50CD89'}}
            placeholder='Scanner le bon de retour ou saisir le n° SAV'
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                chercher()
              }
            }}
          />
          <button
            className='btn btn-success px-6'
            style={{height: 60}}
            onClick={() => chercher()}
            disabled={!saisie.trim()}
          >
            <i className='bi bi-upc-scan fs-1'></i>
          </button>
        </div>

        {resultats && resultats.length > 1 && !choisi && (
          <div className='list-group mb-5'>
            {resultats.map((r) => (
              <button
                key={r.retour.id}
                className='list-group-item list-group-item-action py-4 fs-5'
                onClick={() => choisir(r)}
              >
                <strong>{r.dossier.numero}</strong> ·{' '}
                {nomSavClient(r.dossier.client, r.dossier.contactNom)} ·{' '}
                {r.pieces.map((p) => p.libelle).join(', ')}
              </button>
            ))}
          </div>
        )}

        {choisi && (
          <>
            <div className='card mb-5' style={{border: '2px solid #50CD89'}}>
              <div className='card-body'>
                <div className='d-flex justify-content-between align-items-start'>
                  <div className='fs-2 fw-bold'>
                    SAV <span className='text-success'>#{choisi.dossier.numero}</span>
                  </div>
                  <SavStatutBadge code={choisi.dossier.statut.code} />
                </div>
                <div className='fs-4 fw-bolder text-uppercase mt-2'>
                  {nomSavClient(choisi.dossier.client, choisi.dossier.contactNom)}
                </div>
                <div className='fs-5 mt-3'>
                  {choisi.pieces.length} pièce(s) attendue(s) :{' '}
                  {choisi.pieces
                    .map((p) => `${p.code ? `${p.code} - ` : ''}${p.libelle}`)
                    .join(', ')}
                </div>
                <div className='text-muted mt-2 d-flex align-items-center gap-2'>
                  <SavSourceIcone source={choisi.dossier.source} />
                  {choisi.dossier.centre?.nom} · bon {choisi.retour.numeroBonRetour ?? '—'}
                  {choisi.retour.transporteur && ` · ${choisi.retour.transporteur}`}
                </div>
              </div>
            </div>

            <div className='card mb-5'>
              <div className='card-body'>
                <div className='fw-bold fs-5 mb-3'>Réserves à la réception</div>
                <div className='d-flex gap-3 mb-3'>
                  {caseReserve('reserveColisAbime', 'Colis abîmé')}
                  {caseReserve('reservePieceManquante', 'Pièce manquante')}
                  {caseReserve('reservePieceDifferente', 'Pièce différente')}
                </div>
                <input
                  className='form-control form-control-solid fs-5'
                  style={{height: 50}}
                  placeholder='Détail des réserves'
                  value={reserves.reserveReception}
                  onChange={(e) => setReserves((r) => ({...r, reserveReception: e.target.value}))}
                />
              </div>
            </div>

            <div className='d-flex gap-3'>
              <input
                ref={champPhoto}
                type='file'
                accept='image/*'
                capture='environment'
                className='d-none'
                onChange={(e) => ajouterPhoto(e.target.files)}
              />
              <button
                className='btn btn-light-primary fs-5'
                style={{height: 64, flex: 1}}
                onClick={() => champPhoto.current?.click()}
              >
                <i className='bi bi-camera fs-2'></i> Photo du colis {photos > 0 && `(${photos})`}
              </button>
              <button
                className='btn btn-success fs-4'
                style={{height: 64, flex: 2}}
                disabled={envoi}
                onClick={confirmer}
              >
                Confirmer la réception : passer en Reçu
              </button>
            </div>
          </>
        )}
      </div>

      <div className='col-lg-4'>
        <div className='card h-100'>
          <div className='card-header'>
            <h3 className='card-title'>Retours attendus sur ce site</h3>
          </div>
          <div className='card-body'>
            <select
              className='form-select mb-4'
              value={site}
              onChange={(e) => setSite(e.target.value)}
            >
              <option value=''>Tous mes sites</option>
              {referentiels?.sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </select>
            <div style={{maxHeight: 460, overflowY: 'auto'}}>
              {attendus.length ? (
                attendus.map((a) => (
                  <button
                    key={a.retour.id}
                    className='btn text-start w-100 mb-2 p-3 border'
                    style={{
                      backgroundColor: choisi?.retour.id === a.retour.id ? '#FFF8DD' : '#FFFFFF',
                    }}
                    onClick={() => choisir(a)}
                  >
                    <div className='d-flex justify-content-between'>
                      <span className='fw-bold'>{a.dossier.numero}</span>
                      <span className={a.dossier.enRetard ? 'text-danger fw-bold' : 'text-muted'}>
                        {formatSavDate(a.dossier.dateEcheance)}
                      </span>
                    </div>
                    <div className='text-uppercase fs-7'>
                      {nomSavClient(a.dossier.client, a.dossier.contactNom)}
                    </div>
                    <div className='text-muted fs-7'>
                      {a.pieces.map((p) => p.libelle).join(', ')}
                    </div>
                  </button>
                ))
              ) : (
                <div className='text-muted'>Aucun retour attendu.</div>
              )}
            </div>
            <div className='text-muted fs-7 mt-4'>
              Colis sans bon de retour : rechercher par n° SAV, client ou code pièce.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SavReceptionPage
